import { useCallback, useRef, useState } from 'react';
import { imageContributionsApi } from '@/api/image-contributions.api';
import type { SessionImage } from '@/api/image-contributions.api';
import {
  TOTAL_IMAGES,
  correctCount as countCorrect,
  sessionProgress as progressOf,
  sessionTotal,
  skippedAnswer,
  validatedAnswer,
  type LabelAnswer as SessionAnswer,
  type LabelSessionState as SessionState,
} from './label-images.logic';

/**
 * One "Images" session: create it (10 random images), answer or skip each
 * image, then close it. Answers are sent one by one; a failed send is kept
 * locally only. Moved from hooks/use-label-session.ts, behaviour unchanged.
 */

type UseLabelSessionReturn = {
  state: SessionState;
  sessionId: string | null;
  images: SessionImage[];
  currentImage: number;
  correctAnswers: boolean[];
  selectedCharacter: string;
  totalImages: number;
  sessionProgress: number;
  correctCount: number;
  error: string | null;
  setSelectedCharacter: (char: string) => void;
  startSession: () => Promise<void>;
  validateAnswer: () => void;
  previousImage: () => void;
  skipImage: () => void;
  restartSession: () => Promise<void>;
};

export function useLabelSession(): UseLabelSessionReturn {
  const [state, setState] = useState<SessionState>('idle');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [images, setImages] = useState<SessionImage[]>([]);
  const [currentImage, setCurrentImage] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState<boolean[]>([]);
  const [selectedCharacter, setSelectedCharacter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<SessionAnswer[]>([]);

  const totalImages = sessionTotal(images);
  const sessionProgress = progressOf(correctAnswers, totalImages);
  const correctCount = countCorrect(correctAnswers);

  const startSession = useCallback(async () => {
    setState('loading');
    setError(null);
    try {
      const result = await imageContributionsApi.createSession(TOTAL_IMAGES);
      setSessionId(result.session.id);
      setImages(result.images);
      setCurrentImage(0);
      setCorrectAnswers([]);
      setSelectedCharacter('');
      setAnswers([]);
      setState('active');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start session');
      setState('error');
    }
  }, []);

  const submitAnswerToApi = useCallback(
    async (answer: SessionAnswer) => {
      if (!sessionId) return;
      try {
        await imageContributionsApi.submitAnswer(
          sessionId,
          answer.imageIndex,
          answer.userAnswer,
          answer.expectedCharacter,
          answer.isCorrect,
        );
      } catch {
        // Silently fail: the answer is tracked locally
      }
    },
    [sessionId],
  );

  // Set between a validated answer and the next image (600 ms): a second
  // Enter or click in that window must not send the answer again.
  const pendingRef = useRef(false);

  // Takes the index it leaves, so the end of session is called once, outside
  // a state updater (React may run an updater twice).
  const advance = useCallback(
    (from: number) => {
      pendingRef.current = false;
      if (from < totalImages - 1) {
        setSelectedCharacter('');
        setCurrentImage(from + 1);
        return;
      }
      setState('completing');
      if (sessionId) {
        imageContributionsApi
          .completeSession(sessionId)
          .then(() => setState('completed'))
          .catch(() => setState('completed'));
      } else {
        setState('completed');
      }
    },
    [sessionId, totalImages],
  );

  const validateAnswer = useCallback(() => {
    if (pendingRef.current) return;
    const answer = validatedAnswer(images, currentImage, selectedCharacter);
    if (!answer) return;
    pendingRef.current = true;
    setCorrectAnswers((prev) => [...prev, answer.isCorrect]);
    setAnswers((prev) => [...prev, answer]);
    submitAnswerToApi(answer);
    setTimeout(() => advance(currentImage), 600);
  }, [currentImage, images, selectedCharacter, advance, submitAnswerToApi]);

  const previousImage = useCallback(() => {
    if (pendingRef.current) return;
    setCurrentImage((i) => {
      if (i === 0) return i;
      setSelectedCharacter('');
      setCorrectAnswers((prev) => prev.slice(0, -1));
      setAnswers((prev) => prev.slice(0, -1));
      return i - 1;
    });
  }, []);

  const skipImage = useCallback(() => {
    if (pendingRef.current) return;
    const answer = skippedAnswer(images, currentImage);
    setCorrectAnswers((prev) => [...prev, false]);
    setAnswers((prev) => [...prev, answer]);
    submitAnswerToApi(answer);
    advance(currentImage);
  }, [currentImage, images, advance, submitAnswerToApi]);

  const restartSession = useCallback(async () => {
    await startSession();
  }, [startSession]);

  return {
    state,
    sessionId,
    images,
    currentImage,
    correctAnswers,
    selectedCharacter,
    totalImages,
    sessionProgress,
    correctCount,
    error,
    setSelectedCharacter,
    startSession,
    validateAnswer,
    previousImage,
    skipImage,
    restartSession,
  };
}
